import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

// Import team member images
import drRamezImg from '../../assets/images/Team/Dr. Ramez Al-Sibai.png';
import drFowziaImg from '../../assets/images/Team/Dr. Fowzia Mohamed Ali.png';
import drShimaImg from '../../assets/images/Team/Dr. Shima Anas.png';
import drMohammedImg from '../../assets/images/Team/Dr. Mohammed Abu Sabaa.png';

// All team member images are now imported from assets

const TeamSection = styled.section`
  padding: 80px 0;
  background-color: #3858F9; /* Blue background */
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const TeamHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const TeamTitle = styled.h2`
  font-size: 40px;
  font-weight: bold;
  color: white;
  margin-bottom: 16px;
  text-align: center;
  
  .highlight {
    color: #B4C7FF; /* Light blue for the highlighted text */
  }
`;

const TeamDescription = styled.p`
  font-size: 18px;
  color: #E6EEFF;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.5;
  text-align: center;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const TeamCard = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  }
`;

const CardContent = styled.div`
  padding: 24px;
  text-align: center;
`;

const TeamImage = styled.img`
  width: 100%;
  height: 300px;
  object-fit: cover;
  border-radius: 6px;
  margin-bottom: 16px;
`;

const DoctorName = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
  direction: rtl;
  text-align: center;
`;

const DoctorRole = styled.p`
  color: #3858F9;
  font-weight: 500;
  margin-bottom: 8px;
  direction: rtl;
  text-align: center;
`;

// Experience section removed as requested

// Team member data
const teamMembers = [
  {
    name: "الدكتور رامز سباعي\nDr. Ramez Sibai",
    role: "تقويم الأسنان والفكين\nOrthodontist",
    image: drRamezImg,
  },
  {
    name: "الدكتورة فوزية محمد علي\nDr. Fowzia Mohamed Ali",
    role: "طـبـيـب أسنان عــام\nGeneral Dentist", 
    image: drFowziaImg,
  },
  {
    name: "الـدكـتـورة شيماء أنس\nDr. Shima Anas",
    role: "طـبـيـب أسنان عــام\nGeneral Dentist",
    image: drShimaImg,
  },
  {
    name: "الدكتور محمد أبو سبعة\nDr. Mohammed Abu Sabaa",
    role: "أخصــائي اسـتعاضة سنيـة\nProsthodontist",
    image: drMohammedImg,
  },
];

const Team = ({ id }) => {
  const { t, i18n } = useTranslation();
  
  // Generate SEO-friendly alt texts for team members
  const getTeamMemberAlt = (member, index) => {
    const nameLines = member.name.split('\n');
    const roleLines = member.role.split('\n');
    
    // Get name and role in current language
    const name = i18n.language === 'ar' ? nameLines[0] : nameLines[1] || nameLines[0];
    const role = i18n.language === 'ar' ? roleLines[0] : roleLines[1] || roleLines[0];
    
    // Generate alt text with name, role and clinic name
    return `${name}, ${role} - ${i18n.language === 'en' ? 'National Medical Elite Dental Clinic' : 'عيادة النخبة الوطنية الطبية للأسنان'}`;
  };
  
  return (
    <TeamSection id={id}>
      <Container>
        <TeamHeader dir={i18n.dir()}>
          <TeamTitle dir={i18n.dir()}>
            {t('home.team.title.your')} <span className="highlight">{t('home.team.title.confidence')}</span> {t('home.team.title.for_every_visit')}
          </TeamTitle>
          <TeamDescription dir={i18n.dir()}>
            {t('home.team.description')}
          </TeamDescription>
        </TeamHeader>
        
        <TeamGrid>
          {teamMembers.map((member, index) => (
            <TeamCard key={index}>
              <CardContent>
                <TeamImage 
                  src={member.image} 
                  alt={getTeamMemberAlt(member, index)}
                  loading="lazy"
                  data-alt-key={`team.members.${index}.alt`}
                  data-alt-en={`${member.name.split('\n')[1] || member.name.split('\n')[0]}, ${member.role.split('\n')[1] || member.role.split('\n')[0]} - National Medical Elite Dental Clinic`}
                  data-alt-ar={`${member.name.split('\n')[0]}, ${member.role.split('\n')[0]} - عيادة النخبة الوطنية الطبية للأسنان`}
                />
                <DoctorName>{member.name.split("\n").map((line, i) => (
                  <React.Fragment key={i}>
                    {line}{i < member.name.split("\n").length - 1 && <br />}
                  </React.Fragment>
                ))}</DoctorName>
                <DoctorRole>{member.role.split("\n").map((line, i) => (
                  <React.Fragment key={i}>
                    {line}{i < member.role.split("\n").length - 1 && <br />}
                  </React.Fragment>
                ))}</DoctorRole>
              </CardContent>
            </TeamCard>
          ))}
        </TeamGrid>
      </Container>
    </TeamSection>
  );
};

export default Team;
